import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, KeyRound, Sparkles, Loader2, AlertTriangle, Copy, ExternalLink } from "lucide-react";
import { formatApiError } from "@/lib/api";

const RESEND_COOLDOWN = 90; // 90 detik delay anti-spam

export default function VerifyEmailPage() {
  const { verifyEmail, resendOtp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialEmail = (params.get("email") || "").trim();

  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(initialEmail ? RESEND_COOLDOWN : 0);
  const [fallback, setFallback] = useState(null);  // { code, discord, email_sent }
  const otpInputRef = useRef(null);

  useEffect(() => { if (otpInputRef.current) otpInputRef.current.focus(); }, []);

  // Read any manual-code fallback stashed by RegisterPage
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("kr_manual_code");
      if (!raw) return;
      const obj = JSON.parse(raw);
      if (obj?.email && initialEmail && obj.email.toLowerCase() === initialEmail.toLowerCase()) {
        setFallback({ code: obj.code, discord: obj.discord, email_sent: obj.email_sent });
      }
    } catch (_) { /* ignore */ }
  }, [initialEmail]);

  // Cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !otp) return;
    setLoading(true);
    try {
      await verifyEmail(email.trim(), otp.trim());
      try { sessionStorage.removeItem("kr_manual_code"); } catch (_) { /* ignore */ }
      toast.success("Account verified. Welcome.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
    } finally { setLoading(false); }
  };

  const onResend = async () => {
    if (cooldown > 0 || !email) return;
    setResending(true);
    try {
      const data = await resendOtp(email.trim());
      if (data?.email_sent === false && data?.manual_verification_code) {
        setFallback({
          code: data.manual_verification_code,
          discord: data.discord_founder_url || "",
          email_sent: false,
        });
        toast.info("Kode OTP baru dikirim ke email kamu! Silakan cek inbox/spam.");
      } else {
        toast.success(data?.message || "Kode verifikasi baru telah dikirim ke email kamu!");
      }
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Failed to resend code";
      toast.error(msg);
      const match = /(\d+)\s*second/i.exec(msg);
      if (match) setCooldown(parseInt(match[1], 10));
    } finally { setResending(false); }
  };

  const copyCode = async () => {
    if (!fallback?.code) return;
    try {
      await navigator.clipboard.writeText(fallback.code);
      toast.success("Verification code copied to clipboard.");
    } catch (_) {
      toast.error("Could not copy. Please copy manually.");
    }
  };

  return (
    <PageShell noFooter>
      <div className="min-h-[80vh] grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="font-mystic text-rose-400 text-sm">封印の解放</div>
            <h1 className="font-display text-3xl font-bold text-white mt-1">Verify Your Account</h1>
            <p className="text-zinc-400 text-sm mt-2">Enter the 6-digit code we sent to your email.</p>
          </div>

          {fallback && fallback.code && (
            <div
              className="mb-5 rounded-xl border border-amber-500/40 bg-amber-950/30 p-4 backdrop-blur"
              data-testid="manual-code-fallback"
              role="alert"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-amber-200 font-semibold text-sm">
                    Kode OTP Terkirim ke Email
                  </div>
                  <p className="text-amber-100/80 text-xs mt-1 leading-relaxed">
                    Silakan cek <b>Inbox</b> atau folder <b>Spam</b> email kamu untuk memasukkan kode 6-digit. Jika email terkendala/lambat, kamu bisa menggunakan <b>backup code</b> di bawah ini dan hubungi Founder.
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <code
                      className="flex-1 px-3 py-2 rounded-md bg-black/50 border border-amber-500/30 text-amber-200 font-mono text-lg tracking-[0.25em] text-center select-all"
                      data-testid="manual-code-value"
                    >
                      {fallback.code}
                    </code>
                    <button
                      type="button"
                      onClick={copyCode}
                      className="px-2 py-2 rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30"
                      title="Copy code"
                      data-testid="manual-code-copy"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    {fallback.discord && (
                      <a
                        href={fallback.discord}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 underline underline-offset-2"
                        data-testid="manual-code-discord"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Contact on Discord
                      </a>
                    )}
                    <a
                      href="https://www.facebook.com/KyoukaReimei02"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 underline underline-offset-2"
                      data-testid="manual-code-facebook"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Contact on Facebook
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="glass-strong rounded-xl p-7 space-y-5">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-black/40 border-white/10 text-white"
                  data-testid="verify-email-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider">Verification Code</Label>
              <div className="relative mt-1.5">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  ref={otpInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  required
                  minLength={4}
                  maxLength={8}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  className="pl-10 bg-black/40 border-white/10 text-white tracking-[0.5em] font-mono text-lg"
                  placeholder="••••••"
                  data-testid="verify-otp-input"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1.5">Code expires in 15 minutes.</p>
            </div>
            <Button
              type="submit"
              disabled={loading || !email || !otp}
              className="w-full bg-rose-600 hover:bg-rose-500 text-white font-semibold py-6"
              data-testid="verify-submit-btn"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</>) : (<><Sparkles className="w-4 h-4 mr-2" />Verify Account</>)}
            </Button>
            <div className="flex items-center justify-between text-sm pt-2 border-t border-white/5">
              <button
                type="button"
                onClick={onResend}
                disabled={cooldown > 0 || resending || !email}
                className={`font-semibold ${cooldown > 0 || !email ? "text-zinc-500 cursor-not-allowed" : "text-rose-400 hover:text-rose-300"}`}
                data-testid="verify-resend-btn"
              >
                {resending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Code"}
              </button>
              <Link to="/login" className="text-zinc-400 hover:text-zinc-200" data-testid="verify-go-login">Back to login</Link>
            </div>
          </form>
        </div>
      </div>
    </PageShell>
  );
}
